import type { MouseEvent } from "react";

import styled from "styled-components";

import { ChangeValue, Legend, PixelPlaceholderLoader, PlainTooltip } from "@similarweb/ui-components";
import { Text, Flex } from "@similarweb/ui-components/foundations";

import type { KeywordGroupComparisonLegend } from "./useKeywordListData";
import { formatNumber, formatPercent } from "../../utils/numberFormatters";

const LegendWrapper = styled.span`
    /* Ideally we shouldn't rely on if a node has a child with a class name
    but we can't simply fix that in UI componens without breaking other components, hence this workaround */
    div:has(.legend-text-container) {
        margin-right: 0;
    }
`;

interface Legend extends KeywordGroupComparisonLegend {
    color: string;
}

type Props = {
    isLoading: boolean;
    isEnabled: boolean;
    isTooltipDisabled?: boolean;
    legend: Legend;
    onClick: (legend: Legend) => void;
};

export const KeywordsTrendsWidgetLegend = ({
    isLoading,
    isEnabled,
    isTooltipDisabled = false,
    legend,
    onClick,
}: Props) => {
    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(legend);
    };

    return (
        <Flex
            data-automation="search-trends-overview-widget-legend"
            flexDirection="column"
            gap="4px"
            cursor={isLoading ? "default" : "pointer"}
            onClick={handleClick}
        >
            {isTooltipDisabled ? (
                <Legend
                    isChecked={isEnabled}
                    text={legend.name}
                    labelColor={legend.color}
                />
            ) : (
                <PlainTooltip tooltipContent={legend.name}>
                    <LegendWrapper>
                        <Legend
                            isChecked={isEnabled}
                            text={legend.name}
                            labelColor={legend.color}
                        />
                    </LegendWrapper>
                </PlainTooltip>
            )}
            {isLoading ? (
                <PixelPlaceholderLoader
                    width="110px"
                    height="16px"
                />
            ) : (
                <Flex
                    flexDirection="row"
                    alignItems="center"
                    flexWrap="nowrap"
                    gap="4px"
                >
                    <Text
                        fontVariant="input1"
                        data-automation="legend-value"
                    >
                        {formatNumber(legend.value)}
                    </Text>
                    {legend.change !== 0 ? (
                        <ChangeValue
                            value={formatPercent(legend.change)}
                            isDecrease={legend.change < 0}
                        />
                    ) : null}
                </Flex>
            )}
        </Flex>
    );
};
